import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, tap } from 'rxjs';
import { Deployment } from '../models/deployment';
import { CloudTypes, Entities } from '../models/globals';
import { QuickAccessControl, QuickAccessDeploymentDTO } from '../models/quickAccessControl';
import { UserRoleNames, UserRolesInt } from '../models/users';
import { EnvironmentService } from './environment.service';
import { AuthenticationService } from './authentication.service';

interface MenuEntities {
  name: Entities
  displayName: string
  router: string
  icon: string
  subMenu: SubMenu[]
  role: number[]
}

interface SubMenu {
  name: string
  displayName: string
  router: string
  icon: string
  role: number[]
  fill: boolean
}

@Injectable({
  providedIn: 'root'
})
export class HeaderService {

  public toRedirectSessions: boolean = true;
  public toRedirectDeployments: boolean = true;

  /**
 * Retrieves the list of entities based on the user role.
 * 
 * @param userRole - The role of the user.
 * @returns A promise that resolves to an array of entities.
 */

  async getEntities(userRole: string) {
    let entities = await fetch('assets/config/menu.json').then(async res => { return res.json() });
    entities = entities.filter(en => !en["hidden"]).filter(en => en.role?.length != 0)

    switch (userRole) {
      case UserRoleNames.GlobalAdmin:
        entities = entities.filter(en => en.role.includes(UserRolesInt.GlobalAdmin));
        break;

      case UserRoleNames.DeploymentAdmin:
        entities = entities.filter(en => en.role.includes(UserRolesInt.DeploymentAdmin));
        entities = entities.reduce((acc, current) => {
          return [...acc,{
            ... current,
            subMenu: current.subMenu.filter(sub => sub.role.includes(UserRolesInt.DeploymentAdmin))
          }]
        }, []);
        break;
      
      case UserRoleNames.Reader:
        entities = entities.filter(en => en.role.includes(UserRolesInt.Reader));
        break;
    
      default:
        entities = entities.filter(en => en.role.includes(UserRolesInt.JobOperator));
        entities = entities.reduce((acc, current) => {
          return [...acc,{
            ... current,
            subMenu: current.subMenu.filter(sub => sub.role.includes(UserRolesInt.JobOperator))
          }]
        }, [])
        break;
    }
    return entities;
  }

  constructor(private http: HttpClient,
    private env: EnvironmentService,
    private authenticationService: AuthenticationService
  ) { }

  /////////////////////////\\\\\\\\\\\\\\\\\\\\\\\\\\\\\
  private deploymentList = new BehaviorSubject<Deployment[]>(null);
  public DeploymentList = this.deploymentList.asObservable();

  updateDeploymentList(list: Deployment[]) {
    this.deploymentList.next(list);
  }

  /////////////////////////\\\\\\\\\\\\\\\\\\\\\\\\\\\\\

  private deploymentCloudName = new BehaviorSubject<Deployment>(null);
  public DeploymentCLoudName = this.deploymentCloudName.asObservable();

  updateDeploymentCloudName(deployment) {
    this.deploymentCloudName.next(deployment);
  }

  /////////////////////////\\\\\\\\\\\\\\\\\\\\\\\\\\\\\
  private quickAccessLicenseNames = new BehaviorSubject<string>(null);
  public QuickAccessLicenseNames = this.quickAccessLicenseNames.asObservable();

  private quickAccessDep = new BehaviorSubject<QuickAccessDeploymentDTO[]>([]);
  public QuickAccessDep = this.quickAccessDep.asObservable();

  getQuickAccessDeploymentNames() {
    return this.quickAccessDep.value.map(d => d.name)
  }

  getVdiDeployments() {
    return this.quickAccessDep.value.filter(obj => obj.isVdi).map(d => d.name);
  }

  getQuickAccessDeploymentCheckIsVdi(depName: string) {
    return this.quickAccessDep.value.filter(d => d.name.toLowerCase() === depName.toLowerCase());
  }

  getUserRoleForDeployment(depName) {
    if (this.quickAccessDep.value.length == 0) { return 3 }
    return this.quickAccessDep.value?.find(dep => dep.name == depName)?.userRole;
  }

  getCloudNameForDeployment(depName: string){
    return this.quickAccessDep.value?.find(dep => dep.name == depName)?.cloud;
  }
  
  getStorageNameForDeployment(depName: string){
    return this.quickAccessDep.value?.find(dep => dep.name == depName)?.storageAccountName;
  }

  getValueOfIsLocalStoreForDeployment(depName: string){
    return this.quickAccessDep.value?.find(dep => dep.name == depName)?.isLocalStore.toString();
  }
  
  getDeploymentNames(){
    return this.quickAccessDep.value.map((v) => v.name);
  }

  getQuickAccessDeploymentNamesForSpecificCloudProvider(cloud: string) {
    return this.quickAccessDep.value?.filter(dep => dep?.cloud == cloud && dep?.isVdi)?.map(d => d.name)
  }

  /////////////////////////\\\\\\\\\\\\\\\\\\\\\\\\\\\\\

  getQuickAccessControlSummary(): Observable<any>{
    let baseUrl = this.env.envVariables.httpProtocol + this.env.envVariables.webHost + 'SyncHPCInfoes/QuickAccessControlSummary';
    return this.http.get(baseUrl).pipe(
      tap((data: QuickAccessControl | null) => {
        this.quickAccessDep.next(data.deployments);
        this.quickAccessLicenseNames.next(data.licenseServerNames);
      }),
      catchError((err) => {
        return err;
      })
    )
  }

  getUserNewDate() {
    return new Date(Date.parse((new Date().toLocaleString("en-US", { timeZone: JSON.parse(localStorage.getItem('timezone')) }))));
  }

  getSetTimeZone() {
    this.authenticationService.getTimeZone().subscribe({
      next: data => {
        if (data) {
          localStorage.setItem("timezone", JSON.stringify(data.actionMessage));
        }
        else {
        localStorage.setItem("timezone", JSON.stringify('Asia/Kolkata'));
        }
      },
      error: err => { localStorage.setItem("timezone", JSON.stringify('Asia/Kolkata')); }
    });
  }

  getNewFlash() {
    let baseUrl = this.env.envVariables.httpProtocol + this.env.envVariables.webHost + 'UserInfoes/GetNewsFlash';
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json; charset=utf-8')
      .set('Cache-Control', 'no-cache')
      .set('FromMain', 'false')

    return this.http.get(baseUrl, { headers: headers }); 
  }
}
